import { useCallback, useState } from 'react'
import type { ChamadoDetalheDto } from '@crm/shared'
import { BotaoIcone } from '../../componentes/BotaoIcone'
import { useFecharAoClicarFora } from '../../componentes/fechar-fora'
import { Flutuante } from '../../componentes/Flutuante'
import { IconeInfo, IconeSetor, IconeWhatsApp } from '../../componentes/icones'
import { juntar } from '../../componentes/ui'

/**
 * As três etiquetas que dizem por onde a conversa entrou e em que mãos está:
 * o número da casa, o setor e quem pegou da fila.
 *
 * Miúdas de propósito: são contexto, não conteúdo. Respondem "por onde entrou
 * e com quem está" de relance, e devolvem a largura para os ícones de ação,
 * que é o que se clica.
 */
export function EtiquetasDoAtendimento({ chamado }: { chamado: ChamadoDetalheDto | undefined }) {
  return (
    <>
      {chamado?.conexaoNumero ? (
        <span
          className="flex items-center gap-0.5 whitespace-nowrap rounded-full bg-green-600 px-1.5 py-px text-[10px] font-medium leading-4 text-white"
          title={`Conversa recebida no número ${chamado.conexaoNome}`}
        >
          <IconeWhatsApp className="h-2.5 w-2.5" />
          {chamado.conexaoNumero}
        </span>
      ) : null}

      <span
        className="flex items-center gap-0.5 whitespace-nowrap rounded-full bg-tinta-100 px-1.5 py-px text-[10px] leading-4 text-tinta-600"
        title="Setor do atendimento"
      >
        <IconeSetor className="h-2.5 w-2.5" />
        {chamado?.setorNome ?? 'sem setor'}
      </span>

      <span
        className={juntar(
          'whitespace-nowrap rounded-full px-1.5 py-px text-[10px] leading-4',
          chamado?.responsavelNome ? 'bg-tinta-600 text-tinta-50' : 'bg-amber-100 text-amber-800',
        )}
        title={chamado?.responsavelNome ? 'Quem atende' : 'Ninguém pegou da fila ainda'}
      >
        {chamado?.responsavelNome ?? 'na fila'}
      </span>
    </>
  )
}

/**
 * As mesmas etiquetas, recolhidas num botão, para quando o painel da conversa
 * está estreito.
 *
 * Esconder de vez não servia: "com quem está esta conversa" é pergunta que se
 * faz no meio do atendimento, também no notebook. Recolhidas, continuam a um
 * clique, e a largura vai para o nome e para as ações.
 *
 * Quando a conversa ainda está na fila, um ponto âmbar aparece sobre o botão: é a
 * única das três informações que pede providência, e ela não pode sumir atrás
 * de um ícone neutro. É ponto, e não cor do ícone, porque o `BotaoIcone` não
 * deixa a cor do texto ser trocada por fora.
 */
export function InfoDoAtendimento({ chamado }: { chamado: ChamadoDetalheDto | undefined }) {
  const [aberto, setAberto] = useState(false)
  const fechar = useCallback(() => setAberto(false), [])
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, fechar)
  const naFila = Boolean(chamado) && !chamado?.responsavelNome

  return (
    <div ref={caixa} className="relative">
      <BotaoIcone
        rotulo="Número, setor e atendente"
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        className={aberto ? 'bg-tinta-100 text-tinta-900' : undefined}
      >
        <IconeInfo className="h-4 w-4" />
      </BotaoIcone>
      {naFila ? (
        <span
          className="pointer-events-none absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-superficie"
          aria-hidden="true"
        />
      ) : null}

      <Flutuante
        ancora={caixa}
        aberto={aberto}
        aoFechar={fechar}
        largura={224}
        distancia={4}
        className="space-y-2 rounded-xl bg-superficie p-3 shadow-media ring-1 ring-tinta-200"
      >
        <Linha rotulo="Número">
          {chamado?.conexaoNumero ? (
            <span className="flex items-center gap-1 text-xs text-tinta-800">
              <IconeWhatsApp className="h-3 w-3 text-green-600" />
              {chamado.conexaoNumero}
            </span>
          ) : (
            <span className="text-xs text-tinta-400">-</span>
          )}
        </Linha>
        <Linha rotulo="Setor">
          <span className="text-xs text-tinta-800">{chamado?.setorNome ?? 'sem setor'}</span>
        </Linha>
        <Linha rotulo="Atendente">
          <span
            className={juntar(
              'text-xs',
              chamado?.responsavelNome ? 'text-tinta-800' : 'font-medium text-amber-700',
            )}
          >
            {chamado?.responsavelNome ?? 'na fila, ninguém pegou ainda'}
          </span>
        </Linha>
      </Flutuante>
    </div>
  )
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[11px] uppercase text-tinta-400">{rotulo}</span>
      {children}
    </div>
  )
}
